import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import type { StudentProblemSnapshot } from '@wx-weiqi/contracts';
import {
  CameraIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Cross2Icon,
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '@radix-ui/react-icons';
import { BoardSurface } from './BoardSurface';
import { ReviewAnnotationLayer, type ReviewBrushStroke } from './ReviewAnnotationLayer';
import { VerticalMoveSlider } from './VerticalMoveSlider';
import { researchStoneLabels } from '../domain/fox-kifu-presentation';
import { homeworkBoardFocus } from '../domain/homework-board-focus';
import { downloadBoardJpeg } from '../services/board-capture';
import { lockDrawingPageScroll } from '../services/drawing-scroll-lock';
import {
  HomeworkResultResearchStore,
  type HomeworkResultResearch,
} from '../stores/homework-result-research';

export function StudentHomeworkFullBoard({
  problem,
}: {
  readonly problem: StudentProblemSnapshot;
}) {
  return (
    <BoardSurface
      boardAppearance="wood"
      size={problem.initialPosition.size}
      stones={problem.initialPosition.stones}
      initialAnnotations={problem.initialAnnotations ?? []}
      lastMove={null}
    />
  );
}

/** Native modal keeps focus inside and appears above the surrounding results dialog. */
export function StudentHomeworkBoardDialog({
  problem,
  questionNumber,
  onResearch,
  onClose,
}: {
  readonly problem: StudentProblemSnapshot;
  readonly questionNumber: number;
  readonly onResearch: HomeworkResultResearch;
  readonly onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const [store] = useState(
    () => new HomeworkResultResearchStore(problem.initialPosition, onResearch),
  );
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [brushOpen, setBrushOpen] = useState(false);
  const [strokes, setStrokes] = useState<readonly ReviewBrushStroke[]>([]);
  const [zoomed, setZoomed] = useState(false);
  const [zoomError, setZoomError] = useState<string | null>(null);
  const [maximized, setMaximized] = useState(false);
  const [windowScale, setWindowScale] = useState(1);
  const focus = homeworkBoardFocus(
    problem.initialPosition,
    (problem.initialAnnotations ?? []).map((annotation) => annotation.point),
  );
  const viewport = zoomed ? (focus?.viewport ?? null) : null;

  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !dialogRef.current?.open) return;
      // Downloads can leave focus on body; keep Escape from closing the parent homework list.
      event.preventDefault();
      event.stopPropagation();
      if (maximized) setMaximized(false);
      else onClose();
    };
    document.addEventListener('keydown', escape, true);
    return () => document.removeEventListener('keydown', escape, true);
  }, [maximized, onClose]);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!maximized || dialog === null) return;
    // Scale both axes together; CSS transforms also preserve the board's hit targets.
    const fit = () => {
      if (dialog.offsetWidth === 0 || dialog.offsetHeight === 0) return;
      setWindowScale(
        Math.min(
          (window.innerWidth - 16) / dialog.offsetWidth,
          (window.innerHeight - 16) / dialog.offsetHeight,
        ),
      );
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(dialog);
    window.addEventListener('resize', fit);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [maximized]);
  useLayoutEffect(() => {
    if (!brushOpen) return;
    return lockDrawingPageScroll();
  }, [brushOpen]);
  const board = state.boards[state.cursor]!;
  const activeMoves = state.moves.slice(0, state.cursor);
  const labels = researchStoneLabels(activeMoves, board.stones, board.size);
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => {
      store.cancel();
      dialog?.close();
      if (previous?.isConnected) previous.focus();
    };
  }, [store]);
  const capture = async () => {
    const svg = boardRef.current?.querySelector<SVGSVGElement>('svg.go-board');
    if (svg === null || svg === undefined || capturing) return;
    setCapturing(true);
    setCaptureError(null);
    try {
      await downloadBoardJpeg(
        svg,
        `homework-${problem.problemId}-move-${state.cursor}.jpg`,
        boardRef.current?.querySelector<SVGSVGElement>('.review-annotation-layer'),
      );
    } catch (error: unknown) {
      setCaptureError(userErrorMessage(error, '截图失败，请重试。'));
    } finally {
      setCapturing(false);
    }
  };
  return createPortal(
    <dialog
      ref={dialogRef}
      className="student-homework-board-dialog"
      style={maximized ? { transform: `scale(${windowScale})` } : undefined}
      aria-label="题目完整棋盘"
      onCancel={(event) => {
        event.preventDefault();
        if (maximized) setMaximized(false);
        else onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className="student-homework-board-dialog__layout">
        <aside className="student-homework-board-dialog__progress" aria-label="研究进度">
          <span>进度</span>
          <strong>{state.cursor}</strong>
          <button
            type="button"
            aria-label="下一手"
            title="前进一手"
            className={state.pending && state.cursor < state.moves.length ? 'is-busy' : undefined}
            disabled={state.pending || state.cursor === state.moves.length}
            onClick={() => store.seek(state.cursor + 1)}
          >
            <ChevronUpIcon aria-hidden="true" />
          </button>
          <VerticalMoveSlider
            ariaLabel="研究棋谱进度"
            max={state.moves.length}
            value={state.cursor}
            disabled={state.pending || state.moves.length === 0}
            busy={state.pending && state.moves.length > 0}
            onChange={(cursor) => store.seek(cursor)}
          />
          <button
            type="button"
            aria-label="上一手"
            title="后退一手"
            className={state.pending && state.cursor > 0 ? 'is-busy' : undefined}
            disabled={state.pending || state.cursor === 0}
            onClick={() => store.seek(state.cursor - 1)}
          >
            <ChevronDownIcon aria-hidden="true" />
          </button>
          <small>共 {state.moves.length} 手</small>
        </aside>
        <div
          ref={boardRef}
          className="student-homework-board-dialog__board"
          aria-busy={state.pending}
        >
          <BoardSurface
            boardAppearance="wood"
            size={board.size}
            stones={board.stones}
            initialAnnotations={state.cursor === 0 ? (problem.initialAnnotations ?? []) : []}
            lastMove={null}
            stoneLabels={labels}
            viewport={viewport}
            annotationLayer={
              <ReviewAnnotationLayer
                key={`${state.active}-${brushOpen}`}
                size={board.size}
                open={brushOpen}
                tool="BRUSH"
                color="RED"
                width={8}
                annotations={strokes}
                viewport={viewport}
                onStroke={(stroke) => setStrokes((current) => [...current, stroke])}
                onPoint={() => undefined}
              />
            }
            {...(state.active && !brushOpen
              ? {
                  isActionable: (_point, stone) => stone === null && !state.pending,
                  onPoint: (point) => {
                    void store.place(point);
                  },
                }
              : {})}
          />
        </div>
        <aside className="student-homework-board-dialog__tools">
          <h3>第 {questionNumber} 题</h3>
          <strong className="student-homework-board-dialog__first">
            {problem.initialPosition.currentPlayer === 'BLACK' ? '黑先' : '白先'}
          </strong>
          <div className="student-homework-board-dialog__turn" aria-live="polite">
            <span>当前{board.currentPlayer === 'BLACK' ? '黑棋' : '白棋'}</span>
            <span
              className={`stone-preview stone-preview--${board.currentPlayer.toLowerCase()}`}
              aria-hidden="true"
            />
          </div>
          <div
            className="student-homework-board-dialog__view-actions"
            role="group"
            aria-label="棋盘显示"
          >
            <button
              type="button"
              aria-label={zoomed ? '恢复全盘' : '放大题目角部'}
              aria-pressed={zoomed}
              onClick={() => {
                if (focus === null) {
                  setZoomError('题目跨越多个角部或不在单一角部，无法放大');
                  return;
                }
                setZoomError(null);
                setZoomed((current) => !current);
              }}
            >
              {zoomed ? <ZoomOutIcon aria-hidden="true" /> : <ZoomInIcon aria-hidden="true" />}
              {zoomed ? '还原' : '放大'}
            </button>
            <button
              type="button"
              aria-label={maximized ? '退出全屏' : '全屏'}
              aria-pressed={maximized}
              title="按原比例最大化题目窗口"
              onClick={() => setMaximized((current) => !current)}
            >
              {maximized ? (
                <ExitFullScreenIcon aria-hidden="true" />
              ) : (
                <EnterFullScreenIcon aria-hidden="true" />
              )}
              {maximized ? '退出全屏' : '全屏'}
            </button>
          </div>
          <div className="student-homework-board-dialog__actions">
            <button
              type="button"
              className="student-homework-board-dialog__research"
              aria-pressed={state.active}
              onClick={() => {
                if (state.active) setStrokes([]);
                setBrushOpen(false);
                setCaptureError(null);
                store.toggle();
              }}
            >
              {state.active ? '结束' : '研究'}
            </button>
            <button
              type="button"
              onClick={() => {
                void capture();
              }}
              disabled={capturing || state.pending}
            >
              <CameraIcon aria-hidden="true" />
              {capturing ? '保存中…' : '截图'}
            </button>
            <button
              type="button"
              className="student-homework-board-dialog__brush"
              aria-label="画笔"
              aria-pressed={brushOpen}
              title="画笔：红色，粗细 8；再次点击清除画线并退出画笔"
              disabled={state.pending}
              onClick={() => {
                if (brushOpen) setStrokes([]);
                setBrushOpen((current) => !current);
              }}
            >
              <span aria-hidden="true">🖌️</span>
            </button>
          </div>
          <button
            type="button"
            className="student-homework-board-dialog__close"
            aria-label="关闭完整棋盘"
            onClick={onClose}
          >
            <Cross2Icon aria-hidden="true" />
          </button>
          <div className="student-homework-board-dialog__error" role="alert">
            {[state.error, captureError, zoomError].filter(Boolean).join(' · ')}
          </div>
        </aside>
      </div>
    </dialog>,
    document.body,
  );
}
